import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { useExportRetrocessionsMutation, useGetNAVQuery, useGetRetrocessionsQuery } from 'api/main';
import { listingActionButtonStyle, ORDER_BY } from 'utils/constants';
import { useDebounce } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import RetrocessionRow from './RetrocessionRow';
import {
    ActionButton,
    CustomTablePagination,
    EmptyTableBody,
    FiltersButton,
    ListContainer,
    QuickSearchField,
} from 'components';
import RetrocessionsFilters from './RetrocessionsFilters';
import {
    Box,
    Breadcrumbs,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import * as icons from 'mdi-material-ui';

const columns = [
    {
        id: ORDER_BY.name,
        width: 10,
        label: texts.NOM,
        sortable: false,
    },
    {
        id: 'distributorName',
        width: 10,
        label: texts.DISTRIBUTEUR,
        sortable: false,
    },
    {
        id: 'subscription.investmentCharacteristics.shareSubcategory.name',
        width: 10,
        label: texts.SUBCATEGORY_TYPE,
        sortable: false,
    },
    {
        id: 'subscription.investmentCharacteristics.investingSum',
        width: 10,
        label: texts.SHARES_NUMBER,
        sortable: false,
    },
    {
        id: 'vl',
        width: 10,
        label: texts.RETROCESSIONS_PERIOD_VL,
        sortable: false,
    },
    {
        id: 'periodStartDate',
        width: 10,
        label: texts.RETROCESSIONS_PERIOD_START_DATE,
        sortable: false,
    },
    {
        id: 'periodEndDate',
        width: 10,
        label: texts.RETROCESSIONS_PERIOD_END_DATE,
        sortable: false,
    },
    {
        id: 'amount',
        width: 10,
        label: texts.MONTANT,
        sortable: false,
    },
];

export default function Retrocessions({ fund }) {
    const { fundId } = useParams();

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState({
        page: 1,
        itemsPerPage: 15,
        'distributorCompany.corporateName': '',
        investorName: '',
        'subscription.investmentFund': fundId,
    });
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const {
        data: { retrocessions = [], lastPage } = { retrocessions: [], lastPage: 1 },
        isLoading: dataIsLoading,
    } = useGetRetrocessionsQuery(filters);
    const { data: navs = [] } = useGetNAVQuery({
        pagination: false,
        lastCompleted: 1,
        status: ['completed', 'activated'],
    });
    const [exportRetrocessions, { loading: exportLoading }] = useExportRetrocessionsMutation();
    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);
    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );
    const handlePageChange = useCallback(
        page => () => {
            setFilters(prevState => ({ ...prevState, page }));
        },
        [setFilters]
    );

    useEffect(() => {
        if (debouncedKeyword) {
            setFilters(prevState => ({ ...prevState.filters, investorName: debouncedKeyword }));
        }
    }, [debouncedKeyword]);

    return (
        <ListContainer>
            <Breadcrumbs separator={<icons.MenuRight color="primary" />} aria-label="breadcrumb">
                <Link
                    underline="hover"
                    href="/fund-management"
                    to="/fund-management"
                    component={RouterLink}
                >
                    {texts.FUND}
                </Link>
                <Link
                    underline="hover"
                    href={`/fund-management/${fund?.id}/retrocessions`}
                    to={`/fund-management/${fund?.id}/retrocessions`}
                    component={RouterLink}
                >
                    {texts.RETROCESSIONS} - {fund?.name}
                </Link>
            </Breadcrumbs>
            <Box display="flex" alignItems="center" justifyContent="space-between" paddingY={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
                <ActionButton
                    variant="outlined"
                    sx={listingActionButtonStyle}
                    disabled={exportLoading}
                    onClick={exportRetrocessions}
                >
                    {texts.EXPORTER_LISTE}
                </ActionButton>
            </Box>
            {!dataIsLoading && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <RetrocessionsFilters
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                savedFilters={filters}
                loading={dataIsLoading}
                onClose={() => setFiltersOpen(false)}
            />
            <TableContainer>
                <Table>
                    <colgroup>
                        {columns.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {columns.map(c => (
                                <TableCell key={c.id}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {retrocessions.length === 0 || dataIsLoading ? (
                        <EmptyTableBody
                            colSpan={columns.length}
                            loading={dataIsLoading}
                            message={texts.NO_RESULTS_FOUND}
                        />
                    ) : (
                        <TableBody>
                            {retrocessions.map(s => (
                                <RetrocessionRow
                                    key={s.id}
                                    retrocession={s}
                                    navs={navs}
                                    fundDecimals={fund?.numberOfDecimals}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                loading={dataIsLoading}
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={columns.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </ListContainer>
    );
}

Retrocessions.propTypes = {
    fund: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
};
